import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { WHATSAPP_LINK } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", dates: "", guests: "2", message: "" };

const fieldCls =
  "w-full border border-ivory/20 bg-charcoal/60 px-4 py-3 text-sm text-ivory placeholder:text-ivory/35 focus:border-brass focus:outline-none transition-colors duration-300";

export default function EnquiryDialog() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const handler = () => {
      setDone(false);
      setOpen(true);
    };
    window.addEventListener("jamoon:open-enquiry", handler);
    return () => window.removeEventListener("jamoon:open-enquiry", handler);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something interrupted the send. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="enquiry-dialog"
        className="max-h-[90vh] overflow-y-auto border border-brass/25 bg-forest text-ivory sm:max-w-lg"
      >
        {done ? (
          <div className="py-8 text-center" data-testid="enquiry-success">
            <p className="font-serif text-3xl text-ivory">The forest has your message.</p>
            <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-ivory/70">
              Thank you. Bookings at The Jamoon are taken personally and offline — we will
              write back to you shortly. For a faster reply, reach us on WhatsApp.
            </p>
            <a
              data-testid="enquiry-success-whatsapp-btn"
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex items-center gap-2 border border-brass/40 px-6 py-3 text-xs tracking-[0.2em] text-brass transition-colors duration-300 hover:bg-brass/10"
            >
              <MessageCircle className="h-4 w-4" /> CONTINUE ON WHATSAPP
            </a>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl font-light text-ivory">
                Plan your stay
              </DialogTitle>
              <DialogDescription className="text-sm text-ivory/60">
                Bookings are taken offline — tell us when you'd like to walk in, and we will
                write back personally.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-2 space-y-4" data-testid="enquiry-form">
              <input
                data-testid="enquiry-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Full name"
                className={fieldCls}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  data-testid="enquiry-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Email"
                  className={fieldCls}
                />
                <input
                  data-testid="enquiry-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Phone / WhatsApp"
                  className={fieldCls}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  data-testid="enquiry-dates-input"
                  value={form.dates}
                  onChange={set("dates")}
                  placeholder="Preferred dates (e.g. mid-November)"
                  className={fieldCls}
                />
                <select
                  data-testid="enquiry-guests-select"
                  value={form.guests}
                  onChange={set("guests")}
                  className={fieldCls}
                  aria-label="Number of guests"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <option key={n} value={n} className="bg-charcoal">
                      {n} {n === 1 ? "guest" : "guests"}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                data-testid="enquiry-message-textarea"
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Anything we should know — photography, birding, diet, the walk in…"
                className={fieldCls}
              />
              <p className="text-xs leading-relaxed text-ivory/45">
                Best time to visit is October to March. The final 3.5 km are walked through
                the forest — basic fitness is needed.
              </p>
              <button
                data-testid="enquiry-submit-btn"
                type="submit"
                disabled={sending}
                className="w-full bg-brass py-3.5 text-xs font-semibold tracking-[0.25em] text-charcoal transition-colors duration-300 hover:bg-[#B89B6A] disabled:opacity-60"
              >
                {sending ? "SENDING…" : "SEND ENQUIRY"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
